/**
 * Per-bill tax removal ("No Tax").
 *
 * The owner's rule: removing tax lowers the bill by the tax. Every line ends at its taxable value
 * after discounts, so an MRP-inclusive item (MRP 105 at 5%) costs 100 and a tax-exclusive item
 * (200 + 18%) costs its listed 200. Both fall out of one rule; there is no per-type branching.
 *
 * It must NOT be done by pricing at a 0% rate: for an inclusive price that keeps the full MRP
 * (105) and only relabels it. Tax is computed normally first and removed afterwards.
 *
 * Two ways to record it on the invoice, chosen by the store (`billing.tax_removal_mode`). The
 * customer pays the same amount in both:
 *
 *  - `discount` (GST-safe): the line's total stays the reduced amount, but GST is still charged
 *    INSIDE it. The reduction is a discount on the invoice, so the taxable value and the GST the
 *    store owes are both stated correctly. 100 = 95.24 taxable + 4.76 GST.
 *  - `exempt`: every tax component is zero and the line total is its taxable value. Use only for
 *    supplies that really are exempt (or a store outside GST); a registered store that bills a
 *    taxable item this way still owes the tax.
 *
 * Runs AFTER item and bill discounts and BEFORE the totals, on the till and on the server alike.
 */

import type { Paise } from './money.js';
import type { PricedLine } from './pricing.js';
import type { TaxRate } from './tax.js';
import { computeTax } from './tax.js';
import { roundHalfUp } from './money.js';

export const TAX_REMOVAL_MODES = ['discount', 'exempt'] as const;
export type TaxRemovalMode = (typeof TAX_REMOVAL_MODES)[number];

export function isTaxRemovalMode(value: unknown): value is TaxRemovalMode {
  return typeof value === 'string' && (TAX_REMOVAL_MODES as readonly string[]).includes(value);
}

export interface TaxRemovalResult {
  lines: PricedLine[];
  /** What the bill came down by: the tax that would have been charged. */
  removedPaise: Paise;
}

export function removeTax(
  lines: PricedLine[],
  mode: TaxRemovalMode,
  interState: boolean,
  resolveRate: (line: PricedLine) => TaxRate,
): TaxRemovalResult {
  let removedPaise = 0;
  const result = lines.map((line): PricedLine => {
    // Idempotency: a line that already went through removeTax must not compound.
    // Discount mode tags the line with taxReliefPaise; exempt mode zeroes the tax,
    // so a second run finds total == taxable and removes 0. Either way, never re-apply.
    if (line.taxReliefPaise !== undefined && line.taxReliefPaise !== 0) return line;
    // What the customer pays for this line from now on: its value after every discount.
    const payable = line.taxablePaise;
    const removed = line.totalPaise - payable;
    removedPaise += Math.max(0, removed);

    if (mode === 'exempt') {
      return {
        ...line,
        tax: { taxablePaise: payable, cgstPaise: 0, sgstPaise: 0, igstPaise: 0, cessPaise: 0, gstPaise: 0, totalPaise: payable },
        totalPaise: payable,
      };
    }

    // GST stays inside the reduced amount; the difference is a discount on the invoice.
    const tax = computeTax(payable, resolveRate(line), { inclusive: true, interState });
    const reliefPaise = payable - tax.taxablePaise;
    return {
      ...line,
      billDiscountPaise: line.billDiscountPaise + reliefPaise,
      taxReliefPaise: reliefPaise,
      taxablePaise: tax.taxablePaise,
      tax,
      totalPaise: tax.totalPaise,
      effectiveRateMilliPaise: line.qtyMilli === 0 ? 0 : roundHalfUp((tax.taxablePaise * 1000) / line.qtyMilli),
    };
  });
  return { lines: result, removedPaise };
}

/** The part of the bill discount that is really the tax removal, for showing them apart. */
export function taxReliefOf(lines: PricedLine[]): Paise {
  return lines.reduce((sum, line) => sum + (line.taxReliefPaise ?? 0), 0);
}
